import type {
  LocationQueryRaw,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
  Router
} from 'vue-router';
import type { RouteKey } from '@elegant-router/types';
import { useAuthStore } from '@/store/modules/auth';
import { useRouteStore } from '@/store/modules/route';
import { localStg } from '@/utils/storage/index';

/**
 * create route guard
 *
 * @param router router instance
 */
export function createRouteGuard(router: Router) {
  // @ts-ignore 忽略类型检查
  router.beforeEach(async (to, from, next) => {
    // 防止无限重定向：如果目标是login页面且来源也是login页面，直接允许访问
    if (to.name === 'login' && from.name === 'login') {
      next();
      return;
    }

    // @ts-ignore 忽略类型检查
    const location = await initRoute(to);

    if (location) {
      next(location);
      return;
    }

    const authStore = useAuthStore();

    const rootRoute: RouteKey = 'root';
    const loginRoute: RouteKey = 'login';
    const noAuthorizationRoute: RouteKey = '403';

    const isLogin = Boolean(localStg.get('token'));
    const needLogin = !to.meta.constant;
    const routeRoles = to.meta.roles || [];

    // 确保角色是数组类型
    const userRoles = authStore.userInfo.roles || [];
    const hasRole = userRoles.some(role => (routeRoles as string[]).includes(role));
    const hasAuth = authStore.isStaticSuper || !(routeRoles as string[]).length || hasRole;

    // if it is login route when logged in, then switch to the root page
    if (to.name === loginRoute && isLogin) {
      next({ name: rootRoute });
      return;
    }

    // if the route does not need login, then it is allowed to access directly
    if (!needLogin) {
      handleRouteSwitch(to, from, next);
      return;
    }

    // the route need login but the user is not logged in, then switch to the login page
    if (!isLogin) {
      // 防止重定向循环：如果已经是要去登录页，直接允许访问
      if (to.name === loginRoute) {
        next();
        return;
      }
      next({ name: loginRoute, query: { redirect: to.fullPath } });
      return;
    }

    // if the user is logged in but does not have authorization, then switch to the 403 page
    if (!hasAuth) {
      next({ name: noAuthorizationRoute });
      return;
    }

    // switch route normally
    handleRouteSwitch(to, from, next);
  });
}

/**
 * initialize route
 *
 * @param to to route
 */
async function initRoute(to: RouteLocationNormalized): Promise<RouteLocationRaw | null> {
  const routeStore = useRouteStore();

  const notFoundRoute: RouteKey = 'not-found';
  const isNotFoundRoute = to.name === notFoundRoute;
  const loginRoute: RouteKey = 'login';

  // 防止多次初始化导致的重定向循环
  if (to.name === loginRoute) {
    return null;
  }

  // if the constant route is not initialized, then initialize the constant route
  if (!routeStore.isInitConstantRoute) {
    await routeStore.initConstantRoute();

    // the route is captured by the "not-found" route because the constant route is not initialized
    // after the constant route is initialized, redirect to the original route
    const path = to.fullPath;
    const location: RouteLocationRaw = {
      path,
      replace: true,
      query: to.query,
      hash: to.hash
    };

    return location;
  }

  const isLogin = Boolean(localStg.get('token'));

  if (!isLogin) {
    // if the user is not logged in and the route is a constant route but not the "not-found" route, then it is allowed to access.
    if (to.meta.constant && !isNotFoundRoute) {
      routeStore.onRouteSwitchWhenNotLoggedIn();
      return null;
    }

    // 防止多次重定向到登录页导致的无限循环
    if (to.name === loginRoute) {
      return null;
    }

    // if the user is not logged in, then switch to the login page
    // @ts-ignore 忽略类型检查
    const query = getRouteQueryOfLoginRoute(to, routeStore.routeHome);

    const location: RouteLocationRaw = {
      name: loginRoute,
      query
    };

    return location;
  }

  if (!routeStore.isInitAuthRoute) {
    // initialize the auth route
    await routeStore.initAuthRoute();

    // the route is captured by the "not-found" route because the auth route is not initialized
    // after the auth route is initialized, redirect to the original route
    if (isNotFoundRoute) {
      const rootRoute: RouteKey = 'root';
      const path = to.redirectedFrom?.name === rootRoute ? '/' : to.fullPath;

      const location: RouteLocationRaw = {
        path,
        replace: true,
        query: to.query,
        hash: to.hash
      };

      return location;
    }
  }

  routeStore.onRouteSwitchWhenLoggedIn();

  // the auth route is initialized
  // it is not the "not-found" route, then it is allowed to access
  if (!isNotFoundRoute) {
    return null;
  }

  // it is captured by the "not-found" route, then check whether the route exists
  // @ts-ignore 忽略类型检查
  const exist = await routeStore.getIsAuthRouteExist(to.path);
  const noPermissionRoute: RouteKey = '403';

  if (exist) {
    const location: RouteLocationRaw = {
      name: noPermissionRoute
    };

    return location;
  }

  return null;
}

function handleRouteSwitch(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  // route with href
  if (to.meta.href) {
    window.open(to.meta.href as string, '_blank');

    next({ path: from.fullPath, replace: true, query: from.query, hash: to.hash });

    return;
  }

  next();
}

/**
 * 获取登录路由的查询参数
 * 
 * @param to 目标路由
 * @param homeRouteName 首页路由名称
 */
// @ts-ignore 忽略类型检查
function getRouteQueryOfLoginRoute(to: RouteLocationNormalized, homeRouteName: RouteKey): LocationQueryRaw {
  // 如果目标路由没有名称，或者不是登录页面和首页，则需要重定向
  if (!to.name) {
    return { redirect: to.fullPath };
  }
  
  // 使用字符串比较而不是直接比较Route类型，避开类型检查
  const toRouteName = String(to.name);
  const loginRouteName = 'login';
  const homeRouteNameStr = String(homeRouteName);
  
  // 如果是登录页面或首页，不需要重定向
  if (toRouteName === loginRouteName || toRouteName === homeRouteNameStr) {
    return {};
  }
  
  // 其他页面需要重定向
  return { redirect: to.fullPath };
}
